import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { PageHeader, SectionTitle, EditorialButton } from "@/lib/editorial";
import { CHROME_IMAGES } from "@/data/mockData";
import { blitzApi } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { Zap, CheckCircle2, Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";
import { fmtCET } from "@/lib/utils";

export default function Blitz() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["blitz"], queryFn: blitzApi.get, refetchInterval: 15_000 });

  const masterToggle = useMutation({
    mutationFn: blitzApi.masterToggle,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["blitz"] }),
  });
  const agentToggle = useMutation({
    mutationFn: (id) => blitzApi.toggleAgent(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["blitz"] }),
  });
  const setLimit = useMutation({
    mutationFn: ({ id, dailyLimit }) => blitzApi.setLimit(id, dailyLimit),
    onSuccess: () => { toast("Daily limit saved."); qc.invalidateQueries({ queryKey: ["blitz"] }); },
    onError: (e) => toast.error(e.message),
  });
  const runBlitz = useMutation({
    mutationFn: (id) => blitzApi.run(id),
    onSuccess: (_, id) => { toast(`Blitz started for agent #${id}. Refreshing in 15s…`); setTimeout(() => qc.invalidateQueries({ queryKey: ["blitz"] }), 15_000); },
    onError: (e) => toast.error(e.message),
  });

  const masterOn = !data?.masterOff;

  if (isLoading) return <div className="px-8 py-12 text-[var(--muted-light)]">Loading…</div>;

  return (
    <div className="text-[var(--ink)]">
      <div className="relative bg-[var(--ink)] text-white overflow-hidden">
        <img src={CHROME_IMAGES.headerDecor} alt="" className="chrome-img absolute inset-0 w-full h-full object-cover opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-r from-[var(--ink)] via-[var(--ink)]/80 to-transparent" />
        <div className="relative px-8 lg:px-14 py-16">
          <PageHeader eyebrow="Section 01" tape="Connection" rest="sending"
            sub="Sends LinkedIn invitations to ICP-matched prospects continuously, as guardrail budget allows — not a weekly batch. Volume is paced by each agent's daily limit and spacing, not a calendar."
            dark testId="blitz-title" />
        </div>
      </div>

      {/* Master toggle */}
      <section className="px-8 lg:px-14 py-12 border-b border-[var(--rule-light)]">
        <div className="grid grid-cols-12 gap-8 items-start">
          <div className="col-span-12 md:col-span-7">
            <SectionTitle eyebrow="Schedule">Continuous, availability-driven sending</SectionTitle>
            <p className="text-[14px] text-[var(--muted-light)] leading-relaxed max-w-2xl">
              When enabled, invites go out for every account with automatic mode on — checked every 15 minutes,
              sent only when the agent's daily limit, per-contact spacing, and working-hours window all allow it.
              No note on the invitation (higher acceptance rate). There is no weekly batch: spreading volume out
              across the day and week reads far less roboticly to LinkedIn than one large burst.
            </p>
          </div>
          <div className="col-span-12 md:col-span-5 bg-[var(--paper-2)] border border-[var(--rule-light)] p-6">
            <div className="flex items-center justify-between gap-6">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--muted-light)]">Master toggle</div>
                <div className="font-display text-[22px] mt-1">Enable automatic blitz<br />for all accounts</div>
              </div>
              <Switch checked={masterOn} onCheckedChange={() => masterToggle.mutate()} data-testid="blitz-master-toggle" />
            </div>
          </div>
        </div>
      </section>

      {/* Per-agent cards */}
      <section className="px-8 lg:px-14 py-12 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {(data?.agents ?? []).map((a) => {
          const auto = a.blitzAuto && masterOn;
          return (
            <AgentBlitzCard
              key={a.id}
              agent={a}
              auto={auto}
              masterOn={masterOn}
              onToggle={() => agentToggle.mutate(a.id)}
              onSetLimit={(lim) => setLimit.mutate({ id: a.id, dailyLimit: lim })}
              onRun={() => runBlitz.mutate(a.id)}
              isRunning={runBlitz.isPending && runBlitz.variables === a.id}
            />
          );
        })}
      </section>

      {/* History */}
      <section className="px-8 lg:px-14 pb-16">
        <SectionTitle eyebrow="History">Past blitz runs</SectionTitle>
        <div className="border border-[var(--rule-light)] overflow-y-auto max-h-[320px]">
          <table className="w-full text-[13px]" data-testid="blitz-history">
            <thead className="sticky top-0 bg-white z-10">
              <tr className="text-left border-b border-[var(--ink)]">
                <Th>Date</Th><Th>Agent</Th><Th>Mode</Th>
                <Th align="right">Sent</Th><Th align="right">Failed</Th><Th align="center">OK</Th>
              </tr>
            </thead>
            <tbody>
              {(data?.history ?? []).map((r) => (
                <tr key={r.id} className="border-b border-[var(--rule-light)]">
                  <td className="py-3 pr-4 pl-4 font-mono text-[12px]">{fmtCET(r.startedAt)}</td>
                  <td className="py-3 pr-4">{r.agentName}</td>
                  <td className="py-3 pr-4 text-[12px] uppercase tracking-wider">{r.mode}</td>
                  <td className="py-3 pr-4 text-right font-mono">{r.invitesSent}</td>
                  <td className={`py-3 pr-4 text-right font-mono ${r.invitesFailed > 0 ? "text-rose-600" : "text-[var(--muted-light)]"}`}>{r.invitesFailed}</td>
                  <td className="py-3 pr-4 text-center">
                    {r.ok ? <CheckCircle2 className="w-4 h-4 inline text-emerald-500" /> : <span className="text-rose-500">✗</span>}
                  </td>
                </tr>
              ))}
              {(data?.history ?? []).length === 0 && (
                <tr><td colSpan={6} className="py-6 text-center text-[var(--muted-light)]">No blitz runs yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function AgentBlitzCard({ agent: a, auto, masterOn, onToggle, onSetLimit, onRun, isRunning }) {
  const [editingLimit, setEditingLimit] = useState(false);
  const [limitDraft, setLimitDraft] = useState(String(a.dailyLimit));

  function saveLimit() {
    const v = parseInt(limitDraft, 10);
    if (!isNaN(v) && v > 0) { onSetLimit(v); setEditingLimit(false); }
  }

  return (
    <article className="border border-[var(--rule-light)] bg-white p-6" data-testid={`blitz-card-${a.id}`}>
      <div className="flex items-center gap-3 flex-wrap">
        <div className="font-display text-[22px] leading-none">{a.name}</div>
        <div className="ml-auto flex items-center gap-2 text-[11px] uppercase tracking-[0.2em]">
          <span className={auto ? "text-[var(--blue)]" : "text-[var(--muted-light)]"}>{auto ? "Automatic" : "Manual"}</span>
          <Switch disabled={!masterOn} checked={a.blitzAuto} onCheckedChange={onToggle} data-testid={`blitz-mode-${a.id}`} />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-px mt-6 bg-[var(--rule-light)]">
        {/* Editable daily limit */}
        <div className="px-4 py-4 bg-white">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--muted-light)]">Daily limit</div>
          {editingLimit ? (
            <div className="flex items-center gap-1 mt-1.5">
              <input
                type="number" min="1" max="500"
                value={limitDraft}
                onChange={(e) => setLimitDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") saveLimit(); if (e.key === "Escape") setEditingLimit(false); }}
                className="w-16 border-b-2 border-[var(--blue)] outline-none font-display text-[22px] leading-none bg-transparent"
                autoFocus
              />
              <button onClick={saveLimit} className="text-emerald-500 hover:text-emerald-700"><Check className="w-4 h-4" /></button>
              <button onClick={() => setEditingLimit(false)} className="text-[var(--muted-light)] hover:text-[var(--ink)]"><X className="w-4 h-4" /></button>
            </div>
          ) : (
            <div className="flex items-center gap-2 mt-1.5">
              <span className="font-display text-[28px] leading-none">{a.dailyLimit}</span>
              <button onClick={() => { setLimitDraft(String(a.dailyLimit)); setEditingLimit(true); }}
                className="text-[var(--muted-light)] hover:text-[var(--blue)]">
                <Pencil className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>
        <Stat label="Sent today" value={a.sentToday} />
        <Stat label="Weekly total" value={a.weeklyTotal} />
        <Stat label="Eligible queue" value={a.eligibleCount} highlight />
      </div>

      <div className="mt-6 border-t border-[var(--rule-light)] pt-4 text-[12px] text-[var(--muted-light)]">
        {a.lastRun ? (
          <>
            <span className="uppercase tracking-[0.18em] mr-2">Last blitz</span>
            {fmtCET(a.lastRun.at)} · <span className="font-mono">{a.lastRun.invitesSent} sent</span>
            {a.lastRun.ok ? <CheckCircle2 className="w-3 h-3 inline ml-1 text-emerald-500" /> : <span className="text-rose-500 ml-1">✗</span>}
          </>
        ) : "No blitz run yet."}
      </div>

      <button
        onClick={onRun}
        disabled={a.eligibleCount === 0 || isRunning}
        className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-[var(--ink)] hover:bg-[var(--blue)] disabled:opacity-30 disabled:cursor-not-allowed text-white px-5 py-3 text-[11px] uppercase tracking-[0.24em] transition-colors"
        data-testid={`blitz-run-${a.id}`}
      >
        <Zap className="w-3.5 h-3.5" /> {isRunning ? "Running…" : "Run blitz now"}
      </button>
    </article>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div className={`px-4 py-4 ${highlight ? "bg-[var(--blue)] text-white" : "bg-white"}`}>
      <div className={`text-[10px] uppercase tracking-[0.22em] ${highlight ? "text-white/70" : "text-[var(--muted-light)]"}`}>{label}</div>
      <div className="font-display text-[28px] leading-none mt-1.5">{value}</div>
    </div>
  );
}

function Th({ children, align = "left" }) {
  return <th className={`text-${align} text-[10px] uppercase tracking-[0.22em] text-[var(--muted-light)] py-3 pr-4`}>{children}</th>;
}
